import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import {
  MessageSquare, Send, Check, RefreshCw, Trash2,
  X, Bot, Sparkles,
  ImagePlus, Link as LinkIcon, Search, SlidersHorizontal, ArrowUpDown,
  MoreHorizontal, PenSquare, Users, CheckCheck
} from "lucide-react";
import { useNotifications } from "../contexts/NotificationContext";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Stage colors ── */
const STAGE_COLORS = {
  "Lead":           { text: "#00d404", border: "#00d404" },
  "Agent Recruit":  { text: "#ff786e", border: "#ff786e" },
  "Client":         { text: "#3b82f6", border: "#3b82f6" },
  "Prospect":       { text: "#f59e0b", border: "#f59e0b" },
  "Closed":         { text: "#6b7280", border: "#6b7280" },
  "Team group":     { text: "#8b5cf6", border: "#8b5cf6" },
  "Lead group":     { text: "#0ea5e9", border: "#0ea5e9" },
};
const getStageStyle = (stage) => STAGE_COLORS[stage] || { text: "#888", border: "#888" };


/* ── Font shorthand ── */
const mont = (weight = 400, size = 13) => ({
  fontFamily: "'Montserrat', sans-serif", fontWeight: weight, fontSize: size,
});

export default function Inbox() {
  /* ── State ── */
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiError, setAiError] = useState("");
  const [aiInstructions, setAiInstructions] = useState("");
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [sections, setSections] = useState({ groups: [], campaigns: [] });
  const [attachedImage, setAttachedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const { refreshUnread } = useNotifications();
  const navigate = useNavigate();

  /* ════════════════════════════════════════════════════
     DATA LOADING
     ════════════════════════════════════════════════════ */
  const loadInbox = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      // "all" = no filter (show everything), "groups" = inbox only, others pass through
      const tab = activeTab === "groups" ? "all" : activeTab;
      const r = await axios.get(`${API}/api/messages/inbox`, { params: { tab } });
      setThreads(Array.isArray(r.data) ? r.data : []);
      // If first load returned empty, retry once after a short delay
      if (!isRefresh && Array.isArray(r.data) && r.data.length === 0 && tab === "all") {
        setTimeout(async () => {
          try {
            const r2 = await axios.get(`${API}/api/messages/inbox`, { params: { tab: "all" } });
            if (Array.isArray(r2.data) && r2.data.length > 0) setThreads(r2.data);
          } catch (_) {}
        }, 1500);
      }
    } catch (e) {
      console.error("loadInbox error:", e);
      // Retry once on failure
      if (!isRefresh) {
        setTimeout(async () => {
          try {
            const tab = activeTab === "groups" ? "all" : activeTab;
            const r = await axios.get(`${API}/api/messages/inbox`, { params: { tab } });
            setThreads(Array.isArray(r.data) ? r.data : []);
          } catch (_) {}
          setLoading(false);
        }, 1500);
        return;
      }
    }
    setLoading(false); setRefreshing(false);
  }, [activeTab]);

  const loadMessages = async (contactId) => {
    try {
      const r = await axios.get(`${API}/api/contacts/${contactId}/messages`);
      setMessages(r.data);
    } catch (e) { console.error(e); }
  };

  const markRead = async (contactId) => {
    try {
      await axios.post(`${API}/api/messages/mark-read/${contactId}`);
      setThreads(prev => prev.map(t => t.contact_id === contactId ? { ...t, unread: 0 } : t));
    } catch (e) { console.error(e); }
  };

  const markViewed = async (contactId) => {
    try {
      await axios.post(`${API}/api/messages/mark-viewed/${contactId}`);
      setThreads(prev => prev.map(t => t.contact_id === contactId ? { ...t, viewed: true } : t));
      refreshUnread();
    } catch (e) { console.error(e); }
  };

  const loadSections = async () => {
    try {
      const r = await axios.get(`${API}/api/messages/inbox-sections`);
      setSections(r.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadInbox(); loadSections(); }, [loadInbox, activeTab]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Poll messages when thread open
  useEffect(() => {
    if (!selected) return;
    const interval = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/contacts/${selected.contact_id}/messages`);
        setMessages(prev => {
          if (r.data.length !== prev.length || JSON.stringify(r.data.map(m => m.id)) !== JSON.stringify(prev.map(m => m.id))) return r.data;
          return prev;
        });
      } catch (_) {}
    }, 3000);
    return () => clearInterval(interval);
  }, [selected]);

  // Poll thread list
  useEffect(() => {
    const interval = setInterval(() => loadInbox(true), 5000);
    return () => clearInterval(interval);
  }, [loadInbox, activeTab]);

  // Close overlay on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape" && selected) { setSelected(null); setMessages([]); } };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [selected]);

  /* ════════════════════════════════════════════════════
     THREAD ACTIONS
     ════════════════════════════════════════════════════ */
  const selectThread = async (thread) => {
    // Group rows open the group's own conversation
    if (thread.type === "group") { navigate(`/groups?open=${thread.group_id}`); return; }
    // If clicking the already-selected thread, deselect
    if (selected?.contact_id === thread.contact_id) {
      setSelected(null); setMessages([]); return;
    }
    setSelected(thread);
    loadMessages(thread.contact_id);
    setAiSuggestion(null); setAiError(""); setShowAiPanel(false); setAiInstructions("");
    if (!thread.viewed) markViewed(thread.contact_id);
    if (thread.unread > 0) markRead(thread.contact_id);
  };

  /* ── Checkbox selection ── */
  const toggleCheck = (contactId, e) => {
    if (e) e.stopPropagation();
    if (String(contactId).startsWith("group:")) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId); else next.add(contactId);
      return next;
    });
  };

  const selectAll = () => {
    const allIds = buildTableRows().filter(r => r.type !== "group").map(r => r.contact_id);
    setSelectedIds(new Set(allIds));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!window.confirm(`Delete ${count} conversation${count !== 1 ? "s" : ""}? This removes all messages and cannot be undone.`)) return;
    setDeleting(true);
    try {
      await axios.post(`${API}/api/messages/threads/delete`, { contact_ids: Array.from(selectedIds) });
      if (selected && selectedIds.has(selected.contact_id)) { setSelected(null); setMessages([]); }
      clearSelection();
      await loadInbox(true);
      refreshUnread();
    } catch (e) { alert(e.response?.data?.detail || "Failed to delete threads"); }
    setDeleting(false);
  };

  const deleteCurrentThread = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete this conversation? This removes all messages and cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/messages/thread/${selected.contact_id}`);
      setSelected(null); setMessages([]);
      await loadInbox(true); refreshUnread();
    } catch (e) { alert(e.response?.data?.detail || "Failed to delete thread"); }
  };

  /* ── File upload ── */
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowed.includes(file.type)) { alert("Only JPEG, PNG, GIF, or WebP images allowed"); return; }
    if (file.size > 5 * 1024 * 1024) { alert("Image must be under 5 MB"); return; }
    setUploading(true);
    try {
      const form = new FormData(); form.append("file", file);
      const r = await axios.post(`${API}/api/upload/image`, form);
      setAttachedImage({ url: r.data.url, filename: r.data.filename, preview: URL.createObjectURL(file) });
    } catch (e) { alert(e.response?.data?.detail || "Upload failed"); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUrlUpload = async () => {
    const url = imageUrlInput.trim();
    if (!url) return;
    setUploading(true);
    try {
      const r = await axios.post(`${API}/api/upload/image-url`, { url });
      setAttachedImage({ url: r.data.url, filename: r.data.filename, preview: r.data.url });
      setImageUrlInput(""); setShowUrlInput(false);
    } catch (e) { alert(e.response?.data?.detail || "URL upload failed"); }
    setUploading(false);
  };

  const removeAttachment = () => {
    if (attachedImage?.preview?.startsWith("blob:")) URL.revokeObjectURL(attachedImage.preview);
    setAttachedImage(null);
  };

  /* ── Send / Reply ── */
  const sendReply = async () => {
    if ((!reply.trim() && !attachedImage) || !selected) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/messages/send`, {
        contact_id: selected.contact_id, body: reply,
        ...(attachedImage ? { media_url: attachedImage.url } : {}),
      });
      setReply(""); removeAttachment();
      await loadMessages(selected.contact_id);
      await loadInbox();
    } catch (e) { alert(e.response?.data?.detail || "Send failed"); }
    setSending(false);
  };

  /* ── AI Reply ── */
  const generateAiReply = async () => {
    if (!selected) return;
    setAiSuggesting(true); setAiError(""); setAiSuggestion(null);
    try {
      const payload = { contact_id: selected.contact_id };
      if (aiInstructions.trim()) payload.instructions = aiInstructions.trim();
      const r = await axios.post(`${API}/api/messages/ai-suggest`, payload);
      setAiSuggestion({ text: r.data.suggestion, contactName: r.data.contact_name });
    } catch (e) { setAiError(e.response?.data?.detail || "AI suggestion failed"); }
    setAiSuggesting(false);
  };

  const toggleAiPanel = () => {
    setShowAiPanel(!showAiPanel);
    if (showAiPanel) { setAiSuggestion(null); setAiError(""); }
  };

  const acceptSuggestion = () => {
    if (aiSuggestion) { setReply(aiSuggestion.text); setAiSuggestion(null); setShowAiPanel(false); }
  };

  /* ════════════════════════════════════════════════════
     HELPERS
     ════════════════════════════════════════════════════ */
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    let datePart;
    if (diffDays === 0) datePart = "Today";
    else if (diffDays === 1) datePart = "Yesterday";
    else datePart = d.toLocaleDateString([], { month: "2-digit", day: "2-digit", year: "numeric" });
    const timePart = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return `${datePart}\n${timePart}`;
  };

  const getInitials = (thread) => {
    const f = thread.contact?.first_name?.[0] || thread.contact_name?.split(" ")?.[0]?.[0] || "";
    const l = thread.contact?.last_name?.[0] || thread.contact_name?.split(" ")?.[1]?.[0] || "";
    return (f + l).toUpperCase();
  };

  const getName = (thread) => {
    if (thread.contact?.first_name) return `${thread.contact.first_name} ${thread.contact.last_name || ""}`.trim();
    if (thread.contact_name) return thread.contact_name;
    return "Unknown";
  };

  const getStage = (thread) => thread.contact?.stage || thread.stage || "";

  const getSubtitle = (thread) => thread.type === "group" ? `${thread.member_count || 0} members` : "";

  const getLastMessage = (thread) => {
    if (thread.last_message?.body) {
      const lm = thread.last_message;
      const prefix = lm.direction === "outbound" ? "You: " : (lm.sender_name ? `${lm.sender_name}: ` : "");
      return prefix + thread.last_message.body;
    }
    if (thread.last_message && typeof thread.last_message === "string") {
      const prefix = thread.last_direction === "outbound" ? "You: " : "";
      return prefix + thread.last_message;
    }
    return "";
  };

  const getMessageTime = (thread) => thread.last_message?.created_at || thread.last_time || null;

  /* ── Build unified table rows ── */
  const buildTableRows = () => {
    let rows = [];
    if (activeTab === "groups") {
      threads.filter(t => t.type === "group").forEach(t => { rows.push({ ...t, _type: "group", _groupName: null, _groupColor: null }); });
    } else {
      threads.forEach(t => { rows.push({ ...t, _type: "thread", _groupName: null, _groupColor: null }); });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(r => getName(r).toLowerCase().includes(q) || getLastMessage(r).toLowerCase().includes(q) || getStage(r).toLowerCase().includes(q));
    }
    return rows;
  };

  const tableRows = buildTableRows();
  const hasSelection = selectedIds.size > 0;

  /* ════════════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════════════ */
  const tabs = [
    { id: "all", label: "All Channels" },
    { id: "inbox", label: "Inbox" },
    { id: "sent", label: "Sent" },
    { id: "groups", label: "Groups" },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: "linear-gradient(131.7deg, #eff7ff 0.83%, #ffffff 100%)" }} data-testid="inbox-page">
      <div className="flex-1 flex flex-col p-5 overflow-hidden">
        {/* ── White card ── */}
        <div className="flex-1 flex flex-col bg-white border border-[#dedede] overflow-hidden relative" style={{ borderRadius: 20, padding: 20 }}>

          {/* ── Header row ── */}
          <div className="flex items-start justify-between mb-3 shrink-0">
            <div>
              <h1 style={{ ...mont(700, 24), letterSpacing: "1.2px", color: "#000" }}>Conversations</h1>
              <p style={{ ...mont(300, 10), color: "#000", opacity: 0.85, marginTop: 2 }}>
                All of your client and agent conversations, in one place
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => loadInbox(true)} disabled={refreshing}
                className="flex items-center justify-center text-white w-9 h-9 hover:opacity-90 transition-opacity disabled:opacity-60"
                style={{ backgroundColor: "#555", borderRadius: 2 }}>
                <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
              </button>
              <button className="flex items-center gap-1.5 text-white px-4 py-2 hover:opacity-90 transition-opacity"
                style={{ backgroundColor: "#FF5C35", borderRadius: 2, ...mont(500, 13) }}>
                Compose
                <PenSquare size={14} />
              </button>
            </div>
          </div>

          {/* ── Tabs + Search row ── */}
          <div className="flex items-center justify-between mb-3 shrink-0 gap-4 flex-wrap">
            <div className="flex items-center gap-1.5">
              {tabs.map(tab => {
                const isActive = activeTab === tab.id;
                const isGroups = tab.id === "groups";
                return (
                  <button key={tab.id} onClick={() => { setActiveTab(tab.id); clearSelection(); setSelected(null); setMessages([]); }}
                    className="px-4 py-1.5 transition-colors"
                    style={{
                      ...mont(500, 12), borderRadius: 2,
                      backgroundColor: isActive ? "#FF5C35" : isGroups ? "rgba(0,0,0,0.27)" : "rgba(88,88,88,0.13)",
                      color: isActive ? "#fff" : isGroups ? "#757575" : "#adadad",
                    }}>
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  className="pl-8 pr-3 py-1.5 w-48 focus:outline-none"
                  placeholder="Search"
                  style={{ ...mont(400, 12), backgroundColor: "#e4e4e4", border: "1px solid #c9c9c9", borderRadius: 2 }} />
              </div>
              <button className="flex items-center gap-1.5 px-3 py-1.5 text-white"
                style={{ backgroundColor: "#a3a3a3", borderRadius: 2, ...mont(500, 12) }}>
                <SlidersHorizontal size={12} /> Filter
              </button>
              <button className="flex items-center gap-1.5 px-3 py-1.5 text-white"
                style={{ backgroundColor: "#a3a3a3", borderRadius: 2, ...mont(500, 12) }}>
                <ArrowUpDown size={12} /> Sort
              </button>
            </div>
          </div>

          {/* ── Table + Conversation area ── */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {loading ? (
              <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin" /></div>
            ) : tableRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <MessageSquare size={32} strokeWidth={1} className="mb-3 text-gray-300" />
                <p style={mont(500, 14)}>No conversations found</p>
              </div>
            ) : (
              <>
                {/* Table */}
                <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th className="text-left pl-4 pr-1 py-2.5 rounded-l-sm" style={{ backgroundColor: "#444", width: 36 }}>
                        <input type="checkbox"
                          checked={selectedIds.size === tableRows.length && tableRows.length > 0}
                          onChange={e => e.target.checked ? selectAll() : clearSelection()}
                          className="w-3.5 h-3.5 accent-[#FF5C35] cursor-pointer" />
                      </th>
                      <th className="text-left px-3 py-2.5 text-white" style={{ backgroundColor: "#444", ...mont(400, 13) }}>Name</th>
                      <th className="text-left px-3 py-2.5 text-white" style={{ backgroundColor: "#444", ...mont(400, 13) }}>Message Preview</th>
                      <th className="text-left px-3 py-2.5 text-white" style={{ backgroundColor: "#444", ...mont(400, 13) }}>Channel/Group</th>
                      <th className="text-left px-3 py-2.5 text-white" style={{ backgroundColor: "#444", ...mont(400, 13) }}>Date & Time</th>
                      <th className="text-center px-2 py-2.5 rounded-r-sm" style={{ backgroundColor: "#444", width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row, idx) => {
                      const isActive = selected?.contact_id === row.contact_id;
                      const isChecked = selectedIds.has(row.contact_id);
                      const name = getName(row);
                      const initials = getInitials(row);
                      const stage = getStage(row);
                      const stageStyle = getStageStyle(stage);
                      const lastMsg = getLastMessage(row);
                      const msgTime = getMessageTime(row);
                      const dtStr = formatDateTime(msgTime);
                      const isUnread = row.unread > 0;
                      const groupName = row._groupName;
                      const groupColor = row._groupColor;

                      return (
                        <tr key={row.contact_id || idx}
                          onClick={() => selectThread(row)}
                          className={`cursor-pointer transition-colors ${isActive ? "bg-gray-50" : "hover:bg-gray-50/60"}`}
                          style={{ borderBottom: "1px solid #e2e2e2" }}>
                          {/* Checkbox */}
                          <td className="pl-4 pr-1 py-4" onClick={e => toggleCheck(row.contact_id, e)}>
                            {row.type === "group" ? (
                              <Users size={14} style={{ color: row.channel_color || "#888" }} />
                            ) : (
                            <input type="checkbox" checked={isChecked} readOnly
                              className="w-3.5 h-3.5 accent-[#FF5C35] cursor-pointer pointer-events-none" />
                            )}
                          </td>
                          {/* Name + Avatar + Stage */}
                          <td className="px-3 py-4">
                            <div className="flex items-center gap-2.5">
                              <div className="w-[28px] h-[28px] rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                                style={{ backgroundColor: "#d1e7dd", color: "#1a472a" }}>
                                {initials || "?"}
                              </div>
                              <span style={{ ...mont(isUnread ? 600 : 400, 12), color: "#000", opacity: isUnread ? 1 : 0.6 }}>{name}</span>
                              {getSubtitle(row) && (
                                <span style={{ ...mont(400, 10), color: "#888", whiteSpace: "nowrap" }}>{getSubtitle(row)}</span>
                              )}
                              {stage && (
                                <span className="inline-block px-2 py-px shrink-0"
                                  style={{ ...mont(500, 10), border: `1px solid ${stageStyle.border}`, color: stageStyle.text, borderRadius: 7, whiteSpace: "nowrap" }}>
                                  {stage}
                                </span>
                              )}
                            </div>
                          </td>
                          {/* Message */}
                          <td className="px-3 py-4" style={{ maxWidth: 350 }}>
                            <p className="truncate" style={{ ...mont(isUnread ? 500 : 400, 12), color: "#000", opacity: isUnread ? 0.6 : 0.27 }}>
                              {lastMsg || "—"}
                            </p>
                          </td>
                          {/* Channel/Group */}
                          <td className="px-3 py-4">
                            <span className="inline-block px-2.5 py-0.5 text-white"
                              style={{ ...mont(400, 10), backgroundColor: row.channel_color || groupColor || "#00d404", borderRadius: 2, whiteSpace: "nowrap" }}>
                              {row.channel || groupName || "SMS"}
                            </span>
                          </td>
                          {/* Date & Time */}
                          <td className="px-3 py-4">
                            <span className="whitespace-pre-line" style={{ ...mont(400, 10), color: "#a8a8a8" }}>{dtStr}</span>
                          </td>
                          {/* Actions */}
                          <td className="px-2 py-4 text-center">
                            <button onClick={e => e.stopPropagation()} className="text-gray-400 hover:text-gray-600 p-0.5 hover:bg-gray-100 inline-flex" style={{ borderRadius: 2 }}>
                              <MoreHorizontal size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* table ends here — overlay is rendered outside the scrollable area */}
              </>
            )}
          </div>

          {/* ── Conversation Overlay Panel ── */}
          {selected && (
            <>
              {/* Backdrop */}
              <div className="absolute inset-0 bg-black/20 z-20" style={{ borderRadius: 20 }}
                onClick={() => { setSelected(null); setMessages([]); }} />
              {/* Panel */}
              <div className="absolute bottom-0 left-0 right-0 z-30 bg-white flex flex-col overflow-hidden"
                style={{
                  borderRadius: "20px 20px 20px 20px",
                  height: "65%",
                  boxShadow: "0 -8px 30px rgba(0,0,0,0.12)",
                  animation: "slideUp 0.25s ease-out",
                }}>
                <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>

                {/* Overlay header */}
                <div className="flex items-center gap-3 px-5 py-3.5 border-b border-[#e2e2e2] shrink-0">
                  <div className="w-[30px] h-[30px] rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                    style={{ backgroundColor: "#d1e7dd", color: "#1a472a" }}>
                    {getInitials(selected)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p style={{ ...mont(600, 13), color: "#000" }}>{getName(selected)}</p>
                    <p style={{ ...mont(300, 10), color: "#a8a8a8" }}>
                      {selected?.contact?.phone || selected?.phone} {getStage(selected) ? `· ${getStage(selected)}` : ""}
                    </p>
                  </div>
                  <button onClick={deleteCurrentThread}
                    className="w-8 h-8 flex items-center justify-center hover:bg-red-50 transition-colors text-gray-400 hover:text-red-500"
                    style={{ borderRadius: 2 }} title="Delete conversation" data-testid="delete-thread-btn">
                    <Trash2 size={15} />
                  </button>
                  <button onClick={() => { setSelected(null); setMessages([]); }}
                    className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                    style={{ borderRadius: 2 }} title="Close">
                    <X size={16} />
                  </button>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                  {messages.map(m => {
                    const isOut = m.direction === "outbound";
                    const mediaList = m.media_url ? [{ url: m.media_url }] : (m.media_urls || []);
                    return (
                      <div key={m.id} className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
                        <div style={{ maxWidth: "70%" }}>
                          {isOut && (
                            <div className="text-right mb-0.5">
                              <span style={{ ...mont(400, 9), color: "#FF5C35" }}>
                                {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} {m.status}
                              </span>
                            </div>
                          )}
                          <div className={`text-sm overflow-hidden ${isOut ? "text-white" : "text-gray-900"}`}
                            style={{
                              backgroundColor: isOut ? "#FF5C35" : "#f0f0f0",
                              borderRadius: isOut ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
                              ...(isOut ? { borderRight: "3px solid #e04e2c" } : {}),
                            }}>
                            {mediaList.length > 0 && (
                              <div className={mediaList.length > 1 ? "grid grid-cols-2 gap-0.5" : ""}>
                                {mediaList.map((media, i) => (
                                  <a key={i} href={media.url} target="_blank" rel="noopener noreferrer">
                                    <img src={media.url} alt="MMS" className="w-full max-h-56 object-cover hover:opacity-90 transition-opacity"
                                      onError={e => { e.target.style.display = "none"; }} />
                                  </a>
                                ))}
                              </div>
                            )}
                            <div className="px-4 py-2.5">
                              {m.agent_bot && (
                                <div className="flex items-center gap-1 mb-1">
                                  <Bot size={10} className="text-amber-300" />
                                  <span style={{ ...mont(600, 9), color: isOut ? "rgba(255,255,255,0.7)" : "#b45309" }} className="uppercase tracking-wide">Agent Bot</span>
                                </div>
                              )}
                              {m.body && <p className="leading-relaxed" style={mont(400, 13)}>{m.body}</p>}
                            </div>
                          </div>
                          {!isOut && (
                            <div className="mt-0.5">
                              <span style={{ ...mont(400, 9), color: "#a8a8a8" }}>
                                {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {messages.length === 0 && <div className="text-center py-8 text-gray-400" style={mont(400, 13)}>No messages yet</div>}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply composer */}
                {selected?.contact?.opted_out ? (
                  <div className="px-5 py-3 border-t border-[#e2e2e2] bg-gray-50 text-center" style={{ ...mont(400, 12), color: "#888" }}>
                    This contact has opted out of SMS messages
                  </div>
                ) : (
                  <div className="border-t border-[#e2e2e2] bg-white shrink-0">
                    {/* AI Panel */}
                    {showAiPanel && (
                      <div className="px-5 pt-3 pb-1 border-b border-gray-100 bg-gray-50/80" data-testid="ai-panel">
                        {!aiSuggestion ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <Sparkles size={13} className="text-[#FF5C35]" />
                                <span style={{ ...mont(600, 11), color: "#374151" }}>AI Reply Assistant</span>
                              </div>
                              <button onClick={toggleAiPanel} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
                            </div>
                            <textarea value={aiInstructions} onChange={e => setAiInstructions(e.target.value)}
                              className="w-full border border-gray-200 px-3 py-2 text-xs resize-none focus:outline-none focus:border-[#FF5C35]"
                              rows={2} placeholder={"Tell the AI what to say..."}
                              style={{ ...mont(400, 11), borderRadius: 2 }} data-testid="ai-instructions-input" />
                            <div className="flex gap-2 pb-2">
                              <button onClick={generateAiReply} disabled={aiSuggesting}
                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-[#FF5C35] text-white text-xs hover:bg-[#E84E29] disabled:opacity-60"
                                style={{ ...mont(600, 11), borderRadius: 2 }} data-testid="generate-ai-btn">
                                {aiSuggesting
                                  ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Generating...</>
                                  : <><Sparkles size={12} /> {aiInstructions.trim() ? "Generate with Instructions" : "Generate Reply"}</>}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="mb-2.5 border border-[#FF5C35]/30 bg-[#FFF0EB] overflow-hidden" style={{ borderRadius: 8 }} data-testid="ai-suggestion">
                            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FF5C35]/10 border-b border-[#FF5C35]/20">
                              <Sparkles size={12} className="text-[#FF5C35]" />
                              <span style={{ ...mont(600, 11), color: "#E84E29" }}>AI Suggested Reply</span>
                            </div>
                            <div className="px-3 py-2"><p style={{ ...mont(400, 13), color: "#1f2937" }} className="leading-relaxed">{aiSuggestion.text}</p></div>
                            <div className="flex gap-2 px-3 py-2 border-t border-[#FF5C35]/15">
                              <button onClick={acceptSuggestion} className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-[#FF5C35] text-white hover:bg-[#E84E29]"
                                style={{ ...mont(600, 11), borderRadius: 2 }} data-testid="accept-suggestion"><Check size={12} /> Use This</button>
                              <button onClick={() => setAiSuggestion(null)} className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                                style={{ ...mont(500, 11), borderRadius: 2 }}>Edit</button>
                              <button onClick={generateAiReply} disabled={aiSuggesting} className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                                style={{ ...mont(500, 11), borderRadius: 2 }} data-testid="regenerate-suggestion">
                                <RefreshCw size={11} className={aiSuggesting ? "animate-spin" : ""} /> Retry</button>
                            </div>
                          </div>
                        )}
                        {aiError && (
                          <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 text-xs text-red-700" style={{ borderRadius: 4 }}>
                            <span className="flex-1">{aiError}</span>
                            <button onClick={() => setAiError("")} className="text-red-400 hover:text-red-600"><X size={12} /></button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Attached image preview */}
                    {attachedImage && (
                      <div className="px-5 pt-3 pb-1 border-b border-gray-100 bg-gray-50/50">
                        <div className="inline-flex items-center gap-2 bg-white border border-gray-200 p-1.5 pr-3" style={{ borderRadius: 4 }}>
                          <img src={attachedImage.preview} alt="" className="w-12 h-12 object-cover" style={{ borderRadius: 2 }} />
                          <span className="truncate max-w-[150px]" style={{ ...mont(400, 11), color: "#6b7280" }}>{attachedImage.filename}</span>
                          <button onClick={removeAttachment} className="text-gray-400 hover:text-red-500 ml-1"><X size={14} /></button>
                        </div>
                      </div>
                    )}

                    {/* URL input */}
                    {showUrlInput && (
                      <div className="px-5 pt-2 pb-1 border-b border-gray-100 bg-gray-50/50 flex gap-2 items-center">
                        <input value={imageUrlInput} onChange={e => setImageUrlInput(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleUrlUpload(); }}}
                          className="flex-1 py-1.5 px-3 border border-gray-200 focus:outline-none focus:border-[#FF5C35]"
                          placeholder="Paste image URL..." autoFocus style={{ ...mont(400, 11), borderRadius: 2 }} />
                        <button onClick={handleUrlUpload} disabled={uploading || !imageUrlInput.trim()}
                          className="bg-[#FF5C35] text-white px-3 py-1.5 disabled:opacity-50 hover:bg-[#E84E29]"
                          style={{ ...mont(500, 11), borderRadius: 2 }}>{uploading ? "..." : "Add"}</button>
                        <button onClick={() => { setShowUrlInput(false); setImageUrlInput(""); }} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
                      </div>
                    )}

                    {/* Reply row */}
                    <div className="flex gap-2 px-5 py-3 items-end">
                      <input type="file" ref={fileInputRef} accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={handleFileUpload} />
                      <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                        className={`shrink-0 w-8 h-8 flex items-center justify-center border transition-all ${
                          attachedImage ? "bg-[#FFF0EB] border-[#FF5C35] text-[#FF5C35]" : "bg-white border-gray-200 text-gray-400 hover:text-[#FF5C35] hover:border-[#FF5C35]"
                        } disabled:opacity-50`} title="Attach photo" data-testid="attach-image-btn" style={{ borderRadius: 2 }}>
                        {uploading ? <div className="w-3.5 h-3.5 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin" /> : <ImagePlus size={15} />}
                      </button>
                      <button onClick={toggleAiPanel}
                        className={`shrink-0 w-8 h-8 flex items-center justify-center border transition-all ${
                          showAiPanel ? "bg-[#FFF0EB] border-[#FF5C35] text-[#FF5C35]" : "bg-white border-gray-200 text-gray-400 hover:text-[#FF5C35] hover:border-[#FF5C35]"
                        }`} title="AI Reply Assistant" data-testid="ai-suggest-btn" style={{ borderRadius: 2 }}>
                        <Bot size={15} />
                      </button>
                      <div className="flex-1 relative">
                        <input value={reply} onChange={e => setReply(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); }}}
                          className="w-full py-2 px-3 border border-gray-200 focus:outline-none focus:border-[#FF5C35]"
                          placeholder="Type a reply..."
                          style={{ ...mont(400, 13), borderRadius: 2 }} data-testid="reply-input" />
                      </div>
                      <button onClick={sendReply} disabled={sending || (!reply.trim() && !attachedImage)}
                        className="shrink-0 w-9 h-9 flex items-center justify-center bg-[#FF5C35] text-white hover:bg-[#E84E29] disabled:opacity-40 transition-colors"
                        style={{ borderRadius: "50%" }} data-testid="send-reply-btn">
                        <Send size={15} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Floating Selection Bar ── */}
          {hasSelection && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3 bg-white border border-[#dedede] shadow-xl px-5 py-2.5"
              style={{ borderRadius: 10 }}>
              <span style={{ ...mont(500, 12), color: "#555" }}>
                {selectedIds.size} of {tableRows.length}
              </span>
              <div className="w-px h-5 bg-gray-200" />
              <button onClick={selectAll} className="flex items-center gap-1 hover:bg-gray-50 px-2.5 py-1 transition-colors"
                style={{ ...mont(500, 12), color: "#333", borderRadius: 4 }}>
                <CheckCheck size={14} /> Select All
              </button>
              <div className="w-px h-5 bg-gray-200" />
              <button onClick={deleteSelected} disabled={deleting}
                className="flex items-center gap-1 hover:bg-red-50 px-2.5 py-1 transition-colors disabled:opacity-50"
                style={{ ...mont(500, 12), color: "#ef4444", borderRadius: 4 }}>
                <Trash2 size={14} /> {deleting ? "Deleting..." : "Delete"}
              </button>
              <div className="w-px h-5 bg-gray-200" />
              <button onClick={clearSelection} className="text-gray-400 hover:text-gray-600 p-1"><X size={14} /></button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
